<script setup lang="ts">
import { computed, nextTick, onUnmounted, ref, watch, type CSSProperties } from "vue";
import { useScrollLock } from "@vueuse/core";
import { useI18n } from "vue-i18n";
import type { IAdminPageProps } from "@/utils/adminInterfaces";
import { visibleSettingsSubmenu } from "@/components/shared/settings/settingsSections";

import ListButtons from "@/components/shared/ListButtons.vue";
import { useSentryFeedback } from "@/composables/shared/useSentryFeedback";
import UserLoginCard from "@/components/shared/UserLoginCard.vue";
import type { NavbarItem } from "@/components/shared/navbar/useNavbarItems.ts";

const props = defineProps<
  {
    compacted: boolean;
    drawerLayerStyle: CSSProperties;
    helpItems: NavbarItem[];
    isAdminPage?: boolean;
    itemSize: number;
    menuItems: NavbarItem[];
    navItems: NavbarItem[];
    subNavSections?: Record<string, NavbarItem[]>;
  } & Partial<IAdminPageProps>
>();

const drawerOpen = defineModel<boolean>({ required: true });
const navItemsWithSection = computed<NavbarItem[]>(() =>
  props.navItems.map((item) => {
    const children = item.url ? props.subNavSections?.[item.url] : undefined;
    return children?.length ? { ...item, children } : item;
  })
);

const { t } = useI18n();
const { isDisabled: isFeedbackDisabled, openFeedback } = useSentryFeedback();

const helpItemsWithFeedback = computed<NavbarItem[]>(() => [
  ...props.helpItems,
  {
    title: t("generic.sentry.feedback"),
    icon: "mdi-bug",
    disabled: isFeedbackDisabled.value,
    onClick: () => {
      void openFeedback();
    },
  },
]);

const opened = ref<string[]>([]);
const scrollContainerRef = ref<HTMLElement | null>(null);

const isPageScrollLocked = useScrollLock(document.body);

watch(
  drawerOpen,
  (open) => {
    isPageScrollLocked.value = open;
  },
  { immediate: true }
);

onUnmounted(() => {
  isPageScrollLocked.value = false;
});

watch(drawerOpen, (open) => {
  if (!open) return;

  nextTick(() => {
    requestAnimationFrame(() => {
      scrollContainerRef.value?.scrollTo({ top: 0 });
    });
  });
});
</script>

<template>
  <Teleport to="body">
    <div
      class="nav-mobile-drawer-layer"
      :style="drawerLayerStyle"
    >
      <v-navigation-drawer
        v-model="drawerOpen"
        :border="false"
        class="nav-mobile-drawer"
        data-testid="nav-drawer"
        :elevation="0"
        location="right"
        temporary
        touchless
        width="280"
      >
        <UserLoginCard
          :compacted="compacted"
          :item-size="itemSize"
          @close-drawer="drawerOpen = false"
        />

        <v-divider class="tw:!mt-2" />

        <div
          ref="scrollContainerRef"
          class="tw:min-h-0 tw:flex-1 tw:overflow-y-auto"
        >
          <template v-if="!isAdminPage">
            <ListButtons
              :header="t('generic.navbar.titles.navigation')"
              :items="navItemsWithSection"
            />

            <v-divider />

            <ListButtons
              :header="t('generic.navbar.titles.other')"
              :items="menuItems"
            />

            <v-divider />

            <ListButtons :items="helpItemsWithFeedback" />
          </template>

          <template v-else>
            <v-list v-model:opened="opened">
              <template
                v-for="category in categories ?? []"
                :key="category.key"
              >
                <v-list-item
                  v-if="visibleSettingsSubmenu(category.sections).length === 1"
                  :disabled="
                    category.disabled || visibleSettingsSubmenu(category.sections)[0]?.disabled
                  "
                  :prepend-icon="category.icon"
                  @click="
                    handleDirectSelect?.(
                      visibleSettingsSubmenu(category.sections)[0]!.key,
                      visibleSettingsSubmenu(category.sections)[0]!,
                      category
                    )
                  "
                >
                  {{ t(category.label) }}
                </v-list-item>

                <v-list-group
                  v-else
                  :value="category.key"
                >
                  <template #activator="{ props: activatorProps }">
                    <v-list-item
                      v-bind="activatorProps"
                      :disabled="category.disabled"
                      :prepend-icon="category.icon"
                    >
                      {{ t(category.label) }}
                    </v-list-item>
                  </template>

                  <v-list-item
                    v-for="section in visibleSettingsSubmenu(category.sections)"
                    :key="`${category.key}-${section.key}`"
                    class="tw:!pl-8"
                    :disabled="section.disabled"
                    :prepend-icon="section.icon"
                    :value="`${category.key}-${section.key}`"
                    @click="handleDirectSelect?.(section.key, section, category)"
                  >
                    <v-list-item-title>
                      {{ t(section.label) }}
                    </v-list-item-title>
                  </v-list-item>
                </v-list-group>
              </template>
            </v-list>
          </template>
        </div>
      </v-navigation-drawer>
    </div>
  </Teleport>
</template>

<style>
@reference "@/tailwind-reference.css";

.nav-mobile-drawer-layer {
  position: fixed;
  inset-inline: 0;
  top: var(--nav-drawer-top, 0px);
  bottom: var(--nav-drawer-bottom, 0px);
  overflow: hidden;
  pointer-events: none;
  isolation: isolate;
  @apply tw:z-nav-drawer-layer;
}

.nav-mobile-drawer-layer .nav-mobile-drawer.v-navigation-drawer > .v-navigation-drawer__content {
  display: flex !important;
  flex-direction: column !important;
  height: 100%;
}

.nav-mobile-drawer-layer .nav-mobile-drawer.v-navigation-drawer,
.nav-mobile-drawer-layer
  .nav-mobile-drawer.v-navigation-drawer--temporary.v-navigation-drawer--active {
  position: absolute !important;
  inset-block: 0 !important;
  right: 0 !important;
  left: auto !important;
  height: 100% !important;
  max-height: 100% !important;
  max-width: 100vw !important;
  overflow: visible !important;
  border-top: thin solid rgba(var(--v-border-color), var(--v-border-opacity)) !important;
  border-left: thin solid rgba(var(--v-border-color), var(--v-border-opacity)) !important;
  box-shadow: none !important;
  @apply tw:!z-layer-2;
}

.nav-mobile-drawer-layer .v-navigation-drawer__scrim {
  position: absolute !important;
  inset: 0 !important;
  width: 100% !important;
  height: 100% !important;
  pointer-events: auto;
  @apply tw:!z-layer-1;
}

.nav-mobile-drawer-layer .nav-mobile-drawer.v-navigation-drawer,
.nav-mobile-drawer-layer .nav-mobile-drawer.v-navigation-drawer--active {
  pointer-events: auto;
}
</style>
